import { LogoMark } from "@baze/brand";
import type { PageNode, SectionNode, WebsiteDefinition } from "@baze/schema";
import { Fragment, type CSSProperties, type ReactNode } from "react";
import { domId, messageVars, type RenderContext, type RenderMode } from "./context";
import { waLink } from "./helpers";
import { Icon } from "./icons";
import { derivedNav } from "./nav";
import { componentRegistry, renderSection, type ComponentRegistry } from "./registry";
import { FALLBACK_THEME, motionStyle, textureOf, themeStyle, type EngineTheme } from "./theme";
import { SiteLink, cx } from "./ui";

// ── Root ───────────────────────────────────────────────────────────────────────

export interface SiteRootProps {
  theme: EngineTheme | null | undefined;
  mode?: RenderMode;
  children: ReactNode;
  /** Rendered outside the query container (floating UI). */
  overlay?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Enable CSS motion (scroll reveals, hero entrance). Live renders only; never editor/thumbnails. */
  motion?: boolean;
}

/**
 * `.baze-site` root: theme CSS variables inline, and an inline-size query container that every
 * responsive class (@sm … @5xl) resolves against, so editor frames (390/768/1280) show true layouts.
 */
export function SiteRoot({ theme, mode = "live", children, overlay, className, style, motion = false }: SiteRootProps) {
  return (
    <div className={cx("baze-site", className)} data-mode={mode} data-motion={motion && mode === "live" ? "on" : "off"} data-motion-style={motionStyle(theme)} data-texture={textureOf(theme)} style={{ ...themeStyle(theme), ...style }}>
      <div className="baze-container" style={{ containerType: "inline-size" }}>
        {children}
      </div>
      {overlay}
    </div>
  );
}

// ── Page selection & context ───────────────────────────────────────────────────

const normalizeSlug = (slug: string | undefined) => {
  if (!slug) return "/";
  const s = ("/" + slug.replace(/^\/+/, "")).replace(/\/+$/, "");
  return s === "" ? "/" : s;
};

export function findPage(definition: WebsiteDefinition, pageSlug?: string): PageNode | undefined {
  const slug = normalizeSlug(pageSlug);
  return definition.pages.find((p) => p.slug === slug) ?? definition.pages.find((p) => p.slug === "/") ?? definition.pages[0];
}

const TITLED_TYPES = new Set([
  "hero",
  "about",
  "services",
  "menu",
  "products",
  "gallery",
  "testimonials",
  "faq",
  "stats",
  "opening-hours",
  "location",
  "contact",
  "pricing",
  "cta",
  "timeline",
  "events",
  "team",
  "gift",
  "countdown",
  "links",
  "picks",
]);

function hasTitle(s: SectionNode): boolean {
  const content = s.content as { title?: unknown };
  return TITLED_TYPES.has(s.type) && typeof content.title === "string" && content.title.trim().length > 0;
}

export interface BuildContextOptions {
  mode?: RenderMode;
  siteId?: string;
  selectedSectionId?: string | null;
  hoveredSectionId?: string | null;
  idPrefix?: string;
  thumbnail?: boolean;
  year?: number;
  now?: number;
}

export function buildRenderContext(definition: WebsiteDefinition, page: PageNode, theme: EngineTheme | null | undefined, opts: BuildContextOptions = {}): RenderContext {
  const mode = opts.mode ?? "live";
  const visible = page.sections.filter((s) => !s.hidden);
  const hero = visible.find((s) => s.type === "hero");
  const h1 = hero && hasTitle(hero) ? hero : visible.find(hasTitle);
  const priority = hero && hero.type === "hero" && hero.content.image && hero.variant !== "minimal" ? hero.id : null;
  return {
    definition,
    site: definition.site,
    page,
    theme: theme ?? FALLBACK_THEME,
    mode,
    editor: mode === "editor",
    nav: derivedNav(definition, page),
    h1SectionId: h1?.id ?? null,
    priorityImageSectionId: priority,
    anchors: new Set(visible.map((s) => s.anchor)),
    siteId: opts.siteId,
    selectedSectionId: opts.selectedSectionId ?? null,
    hoveredSectionId: opts.hoveredSectionId ?? null,
    idPrefix: opts.idPrefix ?? "",
    thumbnail: !!opts.thumbnail,
    year: opts.year ?? new Date().getFullYear(),
    now: opts.now ?? Date.now(),
  };
}

// ── Chrome ─────────────────────────────────────────────────────────────────────

export function FloatingWhatsApp({ ctx }: { ctx: RenderContext }) {
  const href = waLink(ctx.site.contact.whatsapp, ctx.site.whatsapp?.defaultMessage, messageVars(ctx));
  if (!href) return null;
  const button = (
    <SiteLink
      ctx={ctx}
      href={href}
      whatsapp
      ariaLabel="Chat WhatsApp"
      className="baze-fab pointer-events-auto grid size-14 place-items-center rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_rgba(0,0,0,0.22)] ring-1 ring-black/5 transition hover:scale-105 hover:shadow-[0_14px_36px_rgba(0,0,0,0.28)] motion-reduce:transition-none"
    >
      <Icon name="whatsapp" className="size-7" />
    </SiteLink>
  );
  if (ctx.editor) {
    // Inside the editor frame: pinned to the bottom of the frame's scrollport, never the app window.
    return (
      <div className="baze-fab-wrap pointer-events-none sticky bottom-0 z-50 h-0" data-baze-floating="">
        <div className="absolute right-4 bottom-4">{button}</div>
      </div>
    );
  }
  return (
    <div className="baze-fab-wrap fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-50" data-baze-floating="">
      {button}
    </div>
  );
}

export function BrandingBadge({ ctx, href }: { ctx: RenderContext; href?: string }) {
  const content = (
    <>
      <LogoMark aria-hidden="true" title="" className="size-4" />
      <span>
        Dibuat dengan <strong className="font-semibold text-site-text">BazeBuild</strong>
      </span>
    </>
  );
  const cls =
    "inline-flex min-h-9 items-center gap-2 rounded-full border border-site-border bg-site-surface px-3.5 text-xs font-medium text-site-muted shadow-site-sm transition-colors hover:text-site-text";
  return (
    <div className="baze-branding flex justify-center bg-site-bg px-5 pt-2 pb-6 text-site-text">
      {href ? (
        <SiteLink ctx={ctx} href={href} className={cls}>
          {content}
        </SiteLink>
      ) : (
        <span className={cls}>{content}</span>
      )}
    </div>
  );
}

// ── Renderer ───────────────────────────────────────────────────────────────────

export interface WebsiteRendererProps {
  definition: WebsiteDefinition;
  /** Resolved theme (`resolveTheme(catalog, definition.theme)`); null renders the neutral fallback. */
  theme: EngineTheme | null | undefined;
  /** Page slug to render ("/", "/tentang"). Defaults to the home page. */
  pageSlug?: string;
  mode?: RenderMode;
  /** Show the "Dibuat dengan BazeBuild" badge (free plan). */
  branding?: boolean;
  brandingHref?: string;
  siteId?: string;
  selectedSectionId?: string | null;
  hoveredSectionId?: string | null;
  /** Show the floating WhatsApp button (default true when a number is set). */
  floatingWhatsapp?: boolean;
  /** Prefix for DOM ids when several renders share a document. */
  idPrefix?: string;
  /** Internal: thumbnail render (lazy images, no chrome). */
  thumbnail?: boolean;
  /** Override the copyright year (tests, deterministic snapshots). */
  year?: number;
  /** Override "now" for countdowns (tests, deterministic snapshots). */
  now?: number;
  /** CSS motion (scroll reveals, entrance). Default: on for live renders, off for editor and thumbnails. */
  motion?: boolean;
  registry?: ComponentRegistry;
  className?: string;
}

export function WebsiteRenderer(props: WebsiteRendererProps) {
  const { definition, theme, mode = "live" } = props;
  const page = findPage(definition, props.pageSlug);
  if (!page) return <SiteRoot theme={theme} mode={mode}>{null}</SiteRoot>;
  const ctx = buildRenderContext(definition, page, theme, {
    mode,
    siteId: props.siteId,
    selectedSectionId: props.selectedSectionId,
    hoveredSectionId: props.hoveredSectionId,
    idPrefix: props.idPrefix,
    thumbnail: props.thumbnail,
    year: props.year,
    now: props.now,
  });
  const registry = props.registry ?? componentRegistry;
  const sections = ctx.editor ? page.sections : page.sections.filter((s) => !s.hidden);
  const head = sections.filter((s) => s.type === "navbar");
  const foot = sections.filter((s) => s.type === "footer");
  const body = sections.filter((s) => s.type !== "navbar" && s.type !== "footer");
  const mainId = domId(ctx, "baze-konten");
  const render = (list: SectionNode[]) => list.map((s, i) => <Fragment key={s.id ?? i}>{renderSection(s, ctx, registry)}</Fragment>);
  const chrome = !ctx.thumbnail;

  return (
    <SiteRoot
      theme={theme}
      mode={mode}
      className={props.className}
      motion={props.motion ?? (mode === "live" && !ctx.thumbnail)}
      overlay={chrome && props.floatingWhatsapp !== false ? <FloatingWhatsApp ctx={ctx} /> : null}
    >
      {chrome && !ctx.editor ? (
        <a href={`#${mainId}`} className="baze-skip">
          Langsung ke konten
        </a>
      ) : null}
      {render(head)}
      <main id={mainId} tabIndex={-1} className="outline-none">
        {ctx.h1SectionId ? null : (
          <h1 className="sr-only">
            {page.title} – {ctx.site.name}
          </h1>
        )}
        {render(body)}
      </main>
      {render(foot)}
      {props.branding ? <BrandingBadge ctx={ctx} href={props.brandingHref} /> : null}
    </SiteRoot>
  );
}

// ── Thumbnail ──────────────────────────────────────────────────────────────────

export interface SiteThumbnailProps {
  definition: WebsiteDefinition;
  theme: EngineTheme | null | undefined;
  /** Rendered width in CSS px. */
  width: number;
  /** Rendered height in CSS px. Defaults to a 16:10 (desktop) or 9:19.5 (mobile) frame. */
  height?: number;
  /** Layout rendered: desktop = 1280px wide, mobile = 390px wide. */
  device?: "desktop" | "mobile";
  pageSlug?: string;
  idPrefix?: string;
  className?: string;
}

/** Scaled, non-interactive live render for dashboard cards and theme pickers. */
export function SiteThumbnail({ definition, theme, width, height, device = "desktop", pageSlug, idPrefix = "thumb-", className }: SiteThumbnailProps) {
  const base = device === "mobile" ? 390 : 1280;
  const scale = width / base;
  const h = height ?? Math.round(device === "mobile" ? (width * 844) / 390 : (width * 10) / 16);
  return (
    <div
      aria-hidden="true"
      inert
      className={cx("baze-thumbnail", className)}
      style={{ width, height: h, overflow: "hidden", position: "relative", pointerEvents: "none", userSelect: "none" }}
    >
      <div style={{ width: base, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <WebsiteRenderer definition={definition} theme={theme} pageSlug={pageSlug} mode="live" thumbnail floatingWhatsapp={false} idPrefix={idPrefix} />
      </div>
    </div>
  );
}
